import { Fragment, FunctionComponent, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { faChevronDown, faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { Spinner } from "@/components/Spinner/Spinner";

import { TestSignalConfigResponse } from "../schema/signal";

interface TestSignalConfigModalProps {
	isOpen: boolean;
	isLoading: boolean;
	result: TestSignalConfigResponse | null;
	error: string | null;
	onClose: () => void;
}

export const TestSignalConfigModal: FunctionComponent<TestSignalConfigModalProps> = ({
	isOpen,
	isLoading,
	result,
	error,
	onClose,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`list.signals.${key}`), [t]);
	const [showRaw, setShowRaw] = useState(false);

	return (
		<Modal
			handleClose={onClose}
			isOpened={isOpen}
			size="lg"
			title={isLoading ? ts("testConfiguration") : ts("testResults")}
		>
			{isLoading && <LoadingView ts={ts} />}
			{error && <ErrorView error={error} />}
			{!isLoading && !error && result && (
				<ResultsView result={result} showRaw={showRaw} setShowRaw={setShowRaw} ts={ts} />
			)}
		</Modal>
	);
};

// --- Sub-components ---

const LoadingView: FunctionComponent<{ ts: (key: string) => string }> = ({ ts }) => (
	<div className="flex flex-col items-center justify-center py-12 gap-4">
		<Spinner color="#4D66F5" />
		<p className="text-sm text-text">{ts("testLoading")}</p>
		<p className="text-xs text-text-meta">{ts("testNoCredits")}</p>
	</div>
);

const ErrorView: FunctionComponent<{ error: string }> = ({ error }) => (
	<div className="p-4 rounded-lg bg-red-50 border border-red-200">
		<p className="text-sm text-red-700">{error}</p>
	</div>
);

interface ResultsViewProps {
	result: TestSignalConfigResponse;
	showRaw: boolean;
	setShowRaw: (v: boolean) => void;
	ts: (key: string) => string;
}

const ResultsView: FunctionComponent<ResultsViewProps> = ({ result, showRaw, setShowRaw, ts }) => (
	<div className="space-y-4">
		{/* Warning banner */}
		{result.warning && (
			<div className="p-3 rounded-lg bg-yellow-50 border border-yellow-300">
				<p className="text-sm text-yellow-800">{result.warning}</p>
			</div>
		)}

		{/* Selected Organization */}
		<div className="p-4 rounded-xl border border-border-light bg-brand-white">
			<h4 className="text-xs font-semibold text-text-meta mb-2">
				{ts("testSelectedOrganization")}
			</h4>
			<div className="flex items-center gap-3">
				<span className="text-sm font-medium text-text">{result.selectedOrganization.name}</span>
				<span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
					{result.selectedOrganization.source}
				</span>
				<span className="text-xs text-text-meta">
					{ts("testQualityScore")}: {result.selectedOrganization.qualityScore}
				</span>
			</div>
			{result.selectedOrganization.websiteUrl && (
				<p className="text-xs text-text-meta mt-1">{result.selectedOrganization.websiteUrl}</p>
			)}
			<p className="text-xs text-text-meta mt-1">
				{ts("testConfigSource")}: <span className="font-medium">{result.configSource}</span>
			</p>
		</div>

		{/* Summary */}
		<SummarySection result={result} ts={ts} />

		{/* Toggle raw/scored */}
		<div className="flex items-center gap-2">
			<button
				className={`text-xs px-3 py-1 rounded-full ${!showRaw ? "bg-blue-600 text-white" : "bg-brand-white text-text-meta"}`}
				onClick={() => setShowRaw(false)}
			>
				{ts("testScoredSignals")}
			</button>
			<button
				className={`text-xs px-3 py-1 rounded-full ${showRaw ? "bg-blue-600 text-white" : "bg-brand-white text-text-meta"}`}
				onClick={() => setShowRaw(true)}
			>
				{ts("testRawSignals")}
			</button>
		</div>

		{/* Signals table */}
		{showRaw ? (
			<RawSignalsTable result={result} ts={ts} />
		) : (
			<ScoredSignalsTable result={result} ts={ts} />
		)}
	</div>
);

const SummarySection: FunctionComponent<{
	result: TestSignalConfigResponse;
	ts: (key: string) => string;
}> = ({ result, ts }) => {
	const { summary } = result;

	return (
		<div className="grid grid-cols-2 gap-4">
			<div className="p-3 rounded-xl border border-border-light">
				<h4 className="text-xs font-semibold text-text-meta mb-2">{ts("testByPriority")}</h4>
				<div className="grid grid-cols-2 gap-2 text-sm">
					<StatItem
						label={ts("testPrio1")}
						value={summary.byPriority.prio1}
						color="text-green-600"
					/>
					<StatItem
						label={ts("testPrio2")}
						value={summary.byPriority.prio2}
						color="text-blue-600"
					/>
					<StatItem
						label={ts("testPrio3")}
						value={summary.byPriority.prio3}
						color="text-yellow-600"
					/>
					<StatItem
						label={ts("testDisqualified")}
						value={summary.byPriority.disqualified}
						color="text-red-600"
					/>
				</div>
			</div>
			<div className="p-3 rounded-xl border border-border-light">
				<h4 className="text-xs font-semibold text-text-meta mb-2">{ts("testByCategory")}</h4>
				<div className="grid grid-cols-1 gap-2 text-sm">
					<StatItem label={ts("testLeadUpdates")} value={summary.byCategory.leadUpdate} />
					<StatItem label={ts("testCompanyUpdates")} value={summary.byCategory.companyUpdate} />
					<StatItem label={ts("testIndustryUpdates")} value={summary.byCategory.industryUpdate} />
				</div>
			</div>
		</div>
	);
};

const StatItem: FunctionComponent<{ label: string; value: number; color?: string }> = ({
	label,
	value,
	color = "text-text",
}) => (
	<div className="flex justify-between items-center">
		<span className="text-xs text-text-meta">{label}</span>
		<span className={`text-sm font-semibold ${color}`}>{value}</span>
	</div>
);

const ScoredSignalsTable: FunctionComponent<{
	result: TestSignalConfigResponse;
	ts: (key: string) => string;
}> = ({ result, ts }) => {
	const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

	const toggleRow = (idx: number) => {
		setExpandedRows((prev) => {
			const next = new Set(prev);

			if (next.has(idx)) {
				next.delete(idx);
			} else {
				next.add(idx);
			}

			return next;
		});
	};

	if (result.scoredSignals.length === 0) {
		return <p className="text-sm text-text-meta py-4 text-center">{ts("testNoSignals")}</p>;
	}

	return (
		<div className="overflow-x-auto rounded-lg border border-border-light">
			<table className="w-full text-xs">
				<thead className="bg-brand-white text-text-meta">
					<tr>
						<th className="px-2 py-2 w-8" />
						<th className="px-3 py-2 text-left">{ts("testTitle")}</th>
						<th className="px-3 py-2 text-left">{ts("testCategory")}</th>
						<th className="px-3 py-2 text-left">{ts("testPriority")}</th>
						<th className="px-3 py-2 text-left">{ts("testReasoning")}</th>
						<th className="px-3 py-2 text-left">{ts("testSource")}</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-border-light">
					{result.scoredSignals.map((signal, idx) => {
						const isExpanded = expandedRows.has(idx);

						return (
							<Fragment key={idx}>
								<tr className="hover:bg-brand-white cursor-pointer" onClick={() => toggleRow(idx)}>
									<td className="px-2 py-2 w-8">
										<Icon
											className="text-text-meta"
											icon={isExpanded ? faChevronDown : faChevronRight}
											size="sm"
										/>
									</td>
									<td className="px-3 py-2 font-medium text-text max-w-[200px] truncate">
										{signal.title}
									</td>
									<td className="px-3 py-2">{signal.signal_category}</td>
									<td className="px-3 py-2">
										<PriorityBadge priority={signal.priority} />
									</td>
									<td className="px-3 py-2 max-w-[250px] truncate">{signal.reasoning}</td>
									<td className="px-3 py-2">
										{signal.source_url ? (
											<a
												className="text-blue-600 underline"
												href={signal.source_url}
												rel="noopener noreferrer"
												target="_blank"
												onClick={(e) => e.stopPropagation()}
											>
												Link
											</a>
										) : (
											"—"
										)}
									</td>
								</tr>
								{isExpanded && (
									<tr className="bg-brand-white">
										<td colSpan={6} className="px-5 py-3">
											<p className="text-xs text-text whitespace-pre-wrap">{signal.reasoning}</p>
										</td>
									</tr>
								)}
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

const RawSignalsTable: FunctionComponent<{
	result: TestSignalConfigResponse;
	ts: (key: string) => string;
}> = ({ result, ts }) => {
	if (result.rawSignals.length === 0) {
		return <p className="text-sm text-text-meta py-4 text-center">{ts("testNoSignals")}</p>;
	}

	return (
		<div className="overflow-x-auto rounded-lg border border-border-light">
			<table className="w-full text-xs">
				<thead className="bg-brand-white text-text-meta">
					<tr>
						<th className="px-3 py-2 text-left">{ts("testTitle")}</th>
						<th className="px-3 py-2 text-left">Summary</th>
						<th className="px-3 py-2 text-left">{ts("testSource")}</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-border-light">
					{result.rawSignals.map((signal, idx) => (
						<tr key={idx} className="hover:bg-brand-white">
							<td className="px-3 py-2 font-medium text-text max-w-[200px] truncate">
								{signal.title}
							</td>
							<td className="px-3 py-2 max-w-[350px] truncate">{signal.summary}</td>
							<td className="px-3 py-2">
								{signal.source_url ? (
									<a
										className="text-blue-600 underline"
										href={signal.source_url}
										rel="noopener noreferrer"
										target="_blank"
									>
										Link
									</a>
								) : (
									"—"
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const PriorityBadge: FunctionComponent<{ priority: string }> = ({ priority }) => {
	const colorMap: Record<string, string> = {
		"Prio 1": "bg-green-100 text-green-700",
		"Prio 2": "bg-blue-100 text-blue-700",
		"Prio 3": "bg-yellow-100 text-yellow-700",
		Disqualified: "bg-red-100 text-red-700",
	};

	const cls = colorMap[priority] ?? "bg-brand-white text-text-meta";

	return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{priority}</span>;
};
